'use client';

import { type ProductSimple } from '@/domain/products/types';
import { useMediaQuery } from '@/hooks';
import { Skeleton } from '@andritzot/metris-web-ui/feedback/skeleton';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import Panels from '@andritzot/metris-web-ui/layout/panels';
import { Drawer } from '@andritzot/metris-web-ui/navigation/drawer';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useSearchParams } from 'next/navigation';
import { lazy, Suspense, useEffect, useMemo } from 'react';

import { useSolutionStore } from '../../store/solution';
import { FilterGroup, type FilterList, type Filters } from '../../types';
import { filterProduct, filterShowType } from '../../utils';
import CardsList from './CardsList';
import Header from './Header';
import SelectedFilters from './SelectedFilters';
import SideFilter from './SideFilter';

const ProductDetailPanel = lazy(() => import('./ProductDetailPanel/ProductDetailPanel'));

interface ProductPageProps {
  products: ProductSimple[];
  filtersValue: Filters[];
  filterList: FilterList;
  isLoggedIn: boolean;
  title?: string;
}

const ProductPage = ({ products, filtersValue, filterList, isLoggedIn, title = 'Solutions' }: ProductPageProps) => {
  const selectedProductId = useSolutionStore((s) => s.selectedProductId);
  const filters = useSolutionStore((s) => s.filters);
  const isSidebarOpen = useSolutionStore((s) => s.isSidebarOpen);
  const showType = useSolutionStore((s) => s.showType);
  const industry = useSolutionStore((s) => s.industry);
  const searchValue = useSolutionStore((s) => s.searchValue);
  const layout = useSolutionStore((s) => s.layout);
  const searchParams = useSearchParams();
  const { PageViews } = usePiwikPro();
  const { isMobile } = useMediaQuery();

  const handleCloseProductDetail = () => useSolutionStore.setState({ selectedProductId: null });

  const productId = searchParams.get('id');

  useEffect(() => {
    PageViews.trackPageView('products');
  }, [PageViews]);

  const productIdFromUrl = productId ? Number.parseInt(productId, 10) : null;

  useEffect(() => {
    if (productIdFromUrl) {
      useSolutionStore.setState({ selectedProductId: productIdFromUrl });
    }
  }, [productIdFromUrl]);

  useEffect(() => {
    useSolutionStore.setState({ filtersValue });
  }, [filtersValue]);

  const filteredProducts = useMemo(
    () =>
      products?.filter((product) => {
        const filteredShowType = filterShowType(product, showType);
        const personas = filterProduct(product, filters, FilterGroup.USER_PERSONA);
        const industries = filterProduct(product, filters, FilterGroup.INDUSTRY, industry);
        const industryAreas = filterProduct(product, filters, FilterGroup.INDUSTRY_AREA, industry);
        const productLine = filterProduct(product, filters, FilterGroup.PRODUCT_LINE);
        const group = filterProduct(product, filters, FilterGroup.PRODUCT_GROUP);
        const benefitGroup = filterProduct(product, filters, FilterGroup.BENEFIT_GROUP);
        const lifeCycleGroup = filterProduct(product, filters, FilterGroup.PLANT_LIFECYCLE_GROUP);
        const lifeCycle = filterProduct(product, filters, FilterGroup.PLANT_LIFECYCLE);
        const businessModel = filterProduct(product, filters, FilterGroup.BUSINESS_MODEL);
        const platform = filterProduct(product, filters, FilterGroup.PLATFORM);
        const countries = filterProduct(product, filters, FilterGroup.COUNTRY);
        const regions = filterProduct(product, filters, FilterGroup.REGION);
        const portfolios = filterProduct(product, filters, FilterGroup.PORTFOLIO);
        const productUnit = filterProduct(product, filters, FilterGroup.PRODUCT_UNIT);
        const search =
          !searchValue ||
          product.product.toUpperCase().includes(searchValue.toUpperCase()) ||
          product.nickName?.toUpperCase().includes(searchValue.toUpperCase());

        return (
          filteredShowType &&
          personas &&
          industries &&
          industryAreas &&
          productLine &&
          group &&
          benefitGroup &&
          lifeCycleGroup &&
          lifeCycle &&
          businessModel &&
          platform &&
          countries &&
          regions &&
          portfolios &&
          productUnit &&
          search
        );
      }) ?? [],
    [filters, industry, products, searchValue, showType],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 32px)',
        overflow: 'hidden',
        backgroundColor: 'background.default',
        p: 4,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Panels.Group>
          <Panels.SidebarPanel
            maxSize="15%"
            minSize="15%"
            collapsible
            isCollapsed={isMobile || !isSidebarOpen}
            onToggleCollapse={(collapsed) => {
              if (!isMobile) useSolutionStore.setState({ isSidebarOpen: !collapsed });
            }}
          >
            <SideFilter
              filterList={filterList}
              filteredProducts={filteredProducts}
              filtersValue={filtersValue}
              isLoggedIn={isLoggedIn}
            />
          </Panels.SidebarPanel>
          <Panels.Separator variant="spacer" />
          <Panels.Item minSize="30%">
            <Box sx={{ pb: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Header title={title} />
              <SelectedFilters />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                  alignContent: 'flex-start',
                  py: 2,
                  pl: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                <CardsList layout={layout} filteredProducts={filteredProducts} />
              </Box>
            </Box>
          </Panels.Item>
          {selectedProductId && !isMobile && (
            <>
              <Panels.Separator variant="spacer" />
              <Panels.Item defaultSize="40%" minSize="35%" surface>
                <Suspense fallback={<Skeleton width="100%" height={16} sx={{ mb: 1 }} />}>
                  <ProductDetailPanel idProduct={selectedProductId} onClose={handleCloseProductDetail} />
                </Suspense>
              </Panels.Item>
            </>
          )}
        </Panels.Group>
      </Box>
      {isMobile && (
        <Drawer
          anchor="right"
          open={!!selectedProductId}
          onClose={handleCloseProductDetail}
          slotProps={{ paper: { sx: { width: '100%', height: '100%' } } }}
        >
          {selectedProductId && (
            <Suspense fallback={<Skeleton width="100%" height={16} sx={{ mb: 1 }} />}>
              <ProductDetailPanel idProduct={selectedProductId} onClose={handleCloseProductDetail} />
            </Suspense>
          )}
        </Drawer>
      )}
    </Box>
  );
};

export default ProductPage;
